/**
 * Institutional sign-in / linking / promotion API client — the fixed wire
 * contract (CONTRACT.txt, 2026-10-06). Every call resolves (never rejects) to
 *
 *   { ok: true,  status, data }
 *   { ok: false, status, code, requestId?, data }
 *
 * `code` is the server's `{code}` body field when it is one this client knows
 * (ACCOUNT_LINK_REQUIRED, REAUTHENTICATION_REQUIRED, MERGE_REVIEW_REQUIRED,
 * OTP_INVALID, …, or a legacy auth-lane alias of one), otherwise a client code
 * derived from the HTTP status, so a dark-flag 404, a proxy HTML error page, an
 * unrecognized server code and an offline network reject all land on a stated
 * terminal state instead of a silent spinner or a generic retry.
 *
 * Paths carry the /qlearn context prefix like every endpoint in urlList.js.
 * The two /auth/institutional/* routes are anonymous; store.js's fetch
 * interceptor strips the Authorization header for them exactly as it does for
 * /auth/phone/*. Everything else rides the signed-in user's bearer token.
 *
 * Authorization is the server's job: the admin calls here are only ever made
 * from a ROLE_ADMIN-gated page, but the server must (and does) reject them for
 * anyone else regardless of what this client sends.
 */
import url from "../../constants/urlList";

export const INSTITUTIONAL_CODES = {
  ACCOUNT_LINK_REQUIRED: "ACCOUNT_LINK_REQUIRED",
  REAUTHENTICATION_REQUIRED: "REAUTHENTICATION_REQUIRED",
  MERGE_REVIEW_REQUIRED: "MERGE_REVIEW_REQUIRED",
  ALREADY_LINKED: "ALREADY_LINKED",
  OTP_INVALID: "OTP_INVALID",
  OTP_EXPIRED: "OTP_EXPIRED",
  OTP_RATE_LIMITED: "OTP_RATE_LIMITED",
  INVALID_INSTITUTIONAL_EMAIL: "INVALID_INSTITUTIONAL_EMAIL",
  INVALID_REQUEST: "INVALID_REQUEST",
  INSTITUTIONAL_DISABLED: "INSTITUTIONAL_DISABLED",
  // Public sign-in only (auth lane InstitutionalAuthErrorCode).
  ACCOUNT_UNAVAILABLE: "ACCOUNT_UNAVAILABLE",
  // Any-address email code only: this address may not sign in by code.
  SIGN_IN_ANOTHER_WAY: "SIGN_IN_ANOTHER_WAY",
  DELIVERY_FAILED: "DELIVERY_FAILED",
  UNAVAILABLE: "UNAVAILABLE",
  // Admin reward queue (promotion lane InstitutionalRewardAdminService).
  AUTHENTICATION_REQUIRED: "AUTHENTICATION_REQUIRED",
  ADMIN_REQUIRED: "ADMIN_REQUIRED",
  REWARD_NOT_FOUND: "REWARD_NOT_FOUND",
  INVALID_METHOD: "INVALID_METHOD",
  INVALID_REFERENCE: "INVALID_REFERENCE",
  ALREADY_FULFILLED: "ALREADY_FULFILLED",
  // Client-derived from the HTTP status (RATE_LIMITED is also an auth-lane code).
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  SERVER_ERROR: "SERVER_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",
  BAD_RESPONSE: "BAD_RESPONSE",
  REQUEST_FAILED: "REQUEST_FAILED",
};

// Contract defaults for a challenge whose response omits the timings.
export const DEFAULT_OTP_EXPIRES_IN_SECONDS = 300;
export const DEFAULT_OTP_RESEND_AFTER_SECONDS = 60;

const SERVER_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,63}$/;

// Names the auth lane sent before root normalized its wire codes (2026-10-07),
// mapped onto the shared vocabulary so an older backend build still gets the
// right UX instead of the generic retry.
const LEGACY_AUTH_CODES = {
  INVALID_CODE: INSTITUTIONAL_CODES.OTP_INVALID,
  INVALID_EMAIL: INSTITUTIONAL_CODES.INVALID_INSTITUTIONAL_EMAIL,
  FEATURE_DISABLED: INSTITUTIONAL_CODES.INSTITUTIONAL_DISABLED,
  RESEND_TOO_SOON: INSTITUTIONAL_CODES.OTP_RATE_LIMITED,
};

const KNOWN_CODES = new Set(Object.values(INSTITUTIONAL_CODES));

/** A known server code, its legacy alias, or null for anything unrecognized. */
export const recognizeServerCode = (code) => {
  if (KNOWN_CODES.has(code)) return code;
  return LEGACY_AUTH_CODES[code] || null;
};

export const codeForStatus = (status) => {
  if (status === 0) return INSTITUTIONAL_CODES.NETWORK_ERROR;
  if (status === 401) return INSTITUTIONAL_CODES.UNAUTHORIZED;
  if (status === 403) return INSTITUTIONAL_CODES.FORBIDDEN;
  if (status === 404) return INSTITUTIONAL_CODES.NOT_FOUND;
  if (status === 409) return INSTITUTIONAL_CODES.CONFLICT;
  if (status === 429) return INSTITUTIONAL_CODES.RATE_LIMITED;
  if (status >= 500) return INSTITUTIONAL_CODES.SERVER_ERROR;
  return INSTITUTIONAL_CODES.REQUEST_FAILED;
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const institutionalFetch = async (path, { method = "GET", body } = {}) => {
  let resp;
  try {
    const opts = { method, headers: { "Content-Type": "application/json" } };
    if (body !== undefined) {
      opts.body = JSON.stringify(body);
    }
    resp = await fetch(`${url.domain}/qlearn${path}`, opts);
  } catch (_) {
    return { ok: false, status: 0, code: INSTITUTIONAL_CODES.NETWORK_ERROR, data: null };
  }
  // The store.js interceptor returns undefined (after starting a redirect to
  // "/") when the stored session has expired; treat that as signed out.
  if (!resp) {
    return { ok: false, status: 401, code: INSTITUTIONAL_CODES.UNAUTHORIZED, data: null };
  }
  const data = await resp.json().catch(() => null);
  if (resp.status >= 200 && resp.status < 300) {
    return { ok: true, status: resp.status, data };
  }
  const serverCode = isPlainObject(data) && typeof data.code === "string"
    && SERVER_CODE_PATTERN.test(data.code) ? data.code : null;
  const requestId = isPlainObject(data) && (typeof data.requestId === "string" || typeof data.requestId === "number")
    ? String(data.requestId) : undefined;
  return {
    ok: false,
    status: resp.status,
    code: recognizeServerCode(serverCode) || codeForStatus(resp.status),
    ...(requestId ? { requestId } : {}),
    data,
  };
};

const positiveSecondsOr = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/**
 * Turn a send-otp response into absolute client deadlines. Deadlines (not
 * tick counters) keep the countdown honest when the tab is backgrounded while
 * the student fetches the code from their mail app.
 */
export const parseChallenge = (data, nowMs = Date.now()) => {
  if (!isPlainObject(data) || data.challengeId === undefined || data.challengeId === null || data.challengeId === "") {
    return null;
  }
  const expiresIn = positiveSecondsOr(data.expiresIn, DEFAULT_OTP_EXPIRES_IN_SECONDS);
  const resendAfter = positiveSecondsOr(data.resendAfter, DEFAULT_OTP_RESEND_AFTER_SECONDS);
  return {
    challengeId: String(data.challengeId),
    expiresAt: nowMs + expiresIn * 1000,
    resendAt: nowMs + resendAfter * 1000,
  };
};

/** A 2xx send-otp whose body carries no challengeId is unusable — surface it. */
const withChallenge = (result) => {
  if (!result.ok) return result;
  const challenge = parseChallenge(result.data);
  if (!challenge) {
    return { ok: false, status: result.status, code: INSTITUTIONAL_CODES.BAD_RESPONSE, data: result.data };
  }
  return { ...result, challenge };
};

const withoutEmpty = (payload) => Object.fromEntries(
  Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== "")
);

// --- Public sign-in (anonymous) -------------------------------------------

export const sendLoginCode = async (email) =>
  withChallenge(await institutionalFetch("/auth/institutional/send-otp", {
    method: "POST",
    body: { email },
  }));

/**
 * Success data is the existing JwtResponse shape (accessToken, id, roles,
 * primaryRole, displayName, email, isProfileComplete, …). referralCode /
 * referralCapturedAt / qlinkState are omitted when absent.
 */
export const verifyLoginCode = ({
  challengeId,
  email,
  otp,
  institution,
  displayName,
  referralCode,
  referralCapturedAt,
  qlinkState,
}) => institutionalFetch("/auth/institutional/verify-otp", {
  method: "POST",
  body: withoutEmpty({
    challengeId,
    email,
    otp,
    institution,
    displayName,
    referralCode,
    referralCapturedAt,
    qlinkState,
  }),
});

// The store interceptor attaches the verified provisional session's token.
export const completeInstitutionalProfile = (name) => institutionalFetch("/profile/complete", {
  method: "POST",
  body: { name },
});

// --- Authenticated linking ------------------------------------------------

/** -> {linked:boolean, email, institution} */
export const getInstitutionalAccount = () => institutionalFetch("/account/institutional");

export const sendLinkCode = async (email) =>
  withChallenge(await institutionalFetch("/account/institutional/send-otp", {
    method: "POST",
    body: { email },
  }));

/**
 * -> {status:"LINKED", email, institution}
 *  | 409 {code:"MERGE_REVIEW_REQUIRED", requestId, message}
 *  | 401 {code:"REAUTHENTICATION_REQUIRED", message}
 */
export const verifyLinkCode = ({ challengeId, email, otp, institution }) =>
  institutionalFetch("/account/institutional/verify-otp", {
    method: "POST",
    body: { challengeId, email, otp, institution },
  });

// --- Promotion --------------------------------------------------------------

export const getReferralProgress = () => institutionalFetch("/institutional/referrals/me");

export const listAdminRewards = () => institutionalFetch("/institutional/admin/rewards");

export const FULFILLMENT_METHODS = ["AMAZON", "CASH"];

// InstitutionalRewardAdminService.MAX_REFERENCE_LENGTH (trimmed, printable).
export const FULFILLMENT_REFERENCE_MAX_LENGTH = 120;

export const fulfillAdminReward = (id, { method, reference }) =>
  institutionalFetch(`/institutional/admin/rewards/${encodeURIComponent(id)}/fulfill`, {
    method: "POST",
    body: { method, reference },
  });
